import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DeleteDialog } from "./DeleteDialog";

interface Props {
  orgSlug: string;
  projectSlug: string;
  rowId: string;
  hostname: string;
  failedReason: string | null;
  onDeleted?: () => void;
}

const FRIENDLY_REASON: Record<string, string> = {
  cert_issuance_timeout: "Certificate issuance timed out. Let's Encrypt may be slow or your DNS regressed.",
  cert_issuance_rate_limited: "Let's Encrypt rate-limited us. Wait an hour and try again.",
  cert_issuance_caa_blocked: "Your domain has a CAA record that blocks Let's Encrypt. Update the CAA record to permit 'letsencrypt.org'.",
  cert_issuance_other: "Certificate issuance failed.",
};

/**
 * Step "failed" — sticky state surfaced from the worker. Retry button
 * re-enqueues the issue job. Deletion lives in Phase 9.
 */
export function FailedStep({ orgSlug, projectSlug, rowId, hostname, failedReason, onDeleted }: Props) {
  const qc = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const retry = useMutation({
    mutationFn: () =>
      api.post(`/orgs/${orgSlug}/projects/${projectSlug}/domains/${rowId}/retry-cert`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-domains", orgSlug, projectSlug] }),
  });
  const friendly = failedReason ? FRIENDLY_REASON[failedReason] ?? failedReason : "Something went wrong.";

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 text-xs rounded-md border border-danger/30 bg-danger-subtle/40 p-3">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-danger flex-shrink-0" />
        <div className="space-y-1">
          <div className="font-semibold">Issuance failed</div>
          <div>{friendly}</div>
          {failedReason && (
            <div className="text-2xs text-subtle-foreground font-mono">code: {failedReason}</div>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDeleteOpen(true)}>
          Delete
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={retry.isPending}
          onClick={() => retry.mutate()}
        >
          {retry.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Retry
        </Button>
      </div>
      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        orgSlug={orgSlug}
        projectSlug={projectSlug}
        rowId={rowId}
        hostname={hostname}
        isLive={false}
        onDeleted={onDeleted}
      />
    </div>
  );
}
